//@flow
import React, {useState, useEffect} from 'react';
import {Autocomplete} from '@material-ui/lab';
import {
  makeStyles,
  Chip,
  Typography,
  FormControlLabel,
  RadioGroup,
  Radio,
} from '@material-ui/core';
import SwitchableSection from '../../../../../elements/SwitchableSection';
import {useCategories} from '../../../../categories/utils';
import CustomTextField from '../../../../../elements/CustomTextField/CustomTextField';
import {HOME_PAGE_SECTION_NAMES} from '../../../utils/home-page-sections/constants';

import type {Category} from '../../../../../entities';
import {
  useProductCategoryLayoutActions,
  useProductCategoryLayout,
} from '../../../utils/product-category-layout';
import {useAlert} from '../../../../../elements/Alert/AlertContext';
import {colors} from '../../../../../assets/styles/colors';
import {useIsFeatureInTier} from '../../../../../utils/component';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import PremiumFeatureBadge from '../../../../../elements/PremiumFeatureBadge/PremiumFeatureBadge';
import {useStoreBusinessModel} from '../../../../store/utils';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.75rem',
  },
  disabledRoot: {
    background: colors.grey300,
  },
  description: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '.75rem',

    [theme.breakpoints.up('md')]: {
      alignItems: 'center',
      flexDirection: 'row',
    },
  },
  subtitle: {
    fontSize: '.875rem',
    color: colors.grey900,
    display: 'block',
    textAlign: 'left',
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    fontSize: '.875rem',
  },
}));

type Props = {
  sectionEnabled?: boolean,
};

const MAX_CATEGORIES = 10;

export default function CategoryAisle(props: Props) {
  const {sectionEnabled} = props;

  const classes = useStyles();
  const {showAlert} = useAlert();

  const categories = useCategories();

  const businessModel = useStoreBusinessModel();

  const hasCategoryLayoutFeature = useIsFeatureInTier(
    'PRODUCT_CATEGORY_LAYOUT'
  );

  const productCategoryLayout = useProductCategoryLayout();

  const [displayType, setDisplayType] = useState<'SUB_CATEGORY' | 'PRODUCT'>(
    'PRODUCT'
  );

  const [categorySelected, setCategorySelected] = useState<Category[]>([]);
  const {
    updateCategoryAisleLayout,
    loading,
  } = useProductCategoryLayoutActions();

  useEffect(() => {
    if (!productCategoryLayout?.productCategoryAisle || !categories) return;
    const {productCategoryAisle} = productCategoryLayout;
    setDisplayType(productCategoryAisle.displayType || 'PRODUCT');

    const categoriesSelected = productCategoryAisle.categories
      ?.map(id => {
        return categories.find(category => category.id === id);
      })
      .filter(Boolean);
    if (!categoriesSelected) return;
    setCategorySelected(categoriesSelected);
  }, [categories, productCategoryLayout]);

  const emptyContent = !categories?.length;

  return (
    <SwitchableSection
      id={HOME_PAGE_SECTION_NAMES.PRODUCT_CATEGORY_AISLE}
      title="Pasillo de categorías"
      subtitle="Muestra los productos por categoría en la página de inicio."
      loading={loading}
      chipLabel={
        !hasCategoryLayoutFeature ? <PremiumFeatureBadge /> : undefined
      }
      classes={{
        root:
          emptyContent || !sectionEnabled ? classes.disabledRoot : undefined,
      }}
    >
      <div className={classes.container}>
        <div className={classes.description}>
          <Typography className={classes.subtitle}>
            Selecciona las categorías que quieres mostrar en el pasillo de
            categorías.
          </Typography>
          <CustomButton href="/admin/categories" variant="text" target="_blank">
            Ir a ordenar categorías
          </CustomButton>
        </div>
        <Autocomplete
          multiple
          id="tags-filled"
          value={categorySelected}
          options={categories}
          onChange={(event, value) => {
            setCategorySelected(value);
            updateCategoryAisleLayout({
              categories: value.map(category => category.id),
              enabled: true,
            })
              .then(() => {
                showAlert('Se ha guardado el menú de categorías', {
                  duration: 5000,
                  type: 'success',
                });
              })
              .catch(() => {
                showAlert('Error al guardar el menú de categorías', {
                  duration: 5000,
                  type: 'error',
                });
              });
          }}
          disabled={!hasCategoryLayoutFeature}
          getOptionDisabled={option => {
            if (categorySelected.length >= MAX_CATEGORIES) {
              return true;
            }
            return !option.enabled;
          }}
          getOptionLabel={option => option.name}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                variant="outlined"
                label={option.name}
                {...getTagProps({index})}
              />
            ))
          }
          renderInput={params => (
            <CustomTextField
              {...params}
              variant="filled"
              multiline
              helperText={
                categorySelected.length === 0 &&
                'Por defecto se muestras las primeras 4 categorías de tu tienda.'
              }
            />
          )}
        />

        <Typography className={classes.subtitle}>
          ¿Qué deseas mostrar en el pasillo de categorías?
        </Typography>
        <RadioGroup
          aria-label="Items de pasillo"
          onChange={e => {
            setDisplayType(e.target.value);
            updateCategoryAisleLayout({
              categories: categorySelected.map(category => category.id),
              displayType: e.target.value,
              enabled: true,
            })
              .then(res => {
                showAlert('Se ha guardado el tipo de contenido', {
                  duration: 5000,
                  type: 'success',
                });
              })
              .catch(err => {
                showAlert('Error al guardar el tipo de contenido', {
                  duration: 5000,
                  type: 'error',
                });
              });
          }}
          value={displayType}
        >
          <FormControlLabel
            value="PRODUCT"
            control={<Radio color="primary" />}
            label={`Mostrar ${
              businessModel === 'PRODUCT' ? 'productos' : 'servicios'
            }`}
          />
          <FormControlLabel
            value="SUB_CATEGORY"
            control={<Radio color="primary" />}
            label="Mostrar subcategorías"
          />
        </RadioGroup>
      </div>
    </SwitchableSection>
  );
}
